import { FocusTrap, Portal } from '@usealloy/component-library';
import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { useSidebarCtx } from './SidebarContext';

// ============================================================
//     TYPES
// ============================================================
export interface ContentWrapperProps extends React.ComponentPropsWithoutRef<'div'> {
  children: React.ReactNode;

  /**
   * Determines how the sidebar is displayed.
   *
   * - `default` (default): A standard block element with sticky @ top: 0
   * - `primary`: Fixed to the left edge of the viewport. Should only be used once for the main navigation sidebar.
   *
   * @default default
   */
  variant?: 'default' | 'primary';

  /**
   * Custom widths for the sidebar open states
   */
  widths?: {
    /**
     * The width of the sidebar when collapsed
     *
     * NOTE: `rem` is recommended over `px` for accessibility consistency
     *
     * @default 5rem
     */
    collapsed?: string;
    /**
     * The width of the sidebar when expanded
     *
     * NOTE: `rem` is recommended over `px` for accessibility consistency
     *
     * @default 15rem
     */
    expanded?: string;
  };
}

// ============================================================
//     STYLED COMPONENTS
// ============================================================
const StyledWrapper = styled.div({
  position: 'sticky',
  top: 0,

  display: 'flex',
  flexDirection: 'column',
  flex: '0 0 auto',

  // FE2.0 declares both 100vh and 100dvh; styled-components object notation can't
  // hold duplicate keys, and dvh is supported by all of Daffy's target browsers
  maxHeight: '100dvh',
  width: 'var(--sidebar-width)',
  maxWidth: '100%',

  whiteSpace: 'nowrap',

  '&[data-expanded="false"]': {
    width: 'var(--sidebar-width-collapsed)',
  },

  '&[data-variant="primary"]': {
    position: 'fixed',
    top: 0,
    bottom: 0,
    left: 0,
    // The primary nav is persistent chrome, not a transient drawer — it needs to stay
    // above things that intentionally render above the `drawer` tier (e.g. the workflow
    // testing suite overlay, at drawer + 1), not just even with it (FRONTEND-741).
    zIndex: 'calc(var(--cl-zindex-drawer) + 2)',
  },

  transitionProperty: 'width',
  transitionDuration: 'var(--cl-duration-400)',
  transitionTimingFunction: 'var(--cl-motion-ease-in-out)',

  '@media print': {
    display: 'none',
  },
});

const StyledFixedPlaceholder = styled.div({
  position: 'relative',
  flex: '0 0 auto',

  width: 'var(--sidebar-width)',
  maxWidth: '100%',

  '&[data-expanded="false"]': {
    width: 'var(--sidebar-width-collapsed)',
  },

  transitionProperty: 'width',
  transitionDuration: 'var(--cl-duration-400)',
  transitionTimingFunction: 'var(--cl-motion-ease-in-out)',
});

const slideIn = keyframes({
  from: { transform: 'translateX(-100%)' },
  to: { transform: 'translateX(0)' },
});

const slideOut = keyframes({
  from: { transform: 'translateX(0)' },
  to: { transform: 'translateX(-100%)' },
});

// Object-notation styled() can't hold a `keyframes` reference (styled-components v4+
// requires the tagged-template form for that), so these two use template literals — same
// approach component-library's own `OverlayMask` uses for its fade-in keyframe.
const StyledMobileWrapper = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: var(--cl-zindex-modal);

  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  height: 100dvh;
  width: var(--sidebar-width, 80vw);
  max-width: 100%;

  white-space: nowrap;

  animation-name: ${slideIn};
  animation-duration: var(--cl-duration-300);
  animation-timing-function: var(--cl-motion-ease-in-out);
  animation-fill-mode: forwards;

  &[data-closing='true'] {
    animation-name: ${slideOut};
  }
`;

const overlayFadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const overlayFadeOut = keyframes({
  from: { opacity: 1 },
  to: { opacity: 0 },
});

const StyledMobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: var(--cl-zindex-overlay-mask);
  background-color: rgba(0, 0, 0, 0.3); /* matches component-library's OverlayMask */

  animation-name: ${overlayFadeIn};
  animation-duration: var(--cl-duration-300);
  animation-timing-function: var(--cl-motion-ease-in-out);
  animation-fill-mode: forwards;

  &[data-closing='true'] {
    animation-name: ${overlayFadeOut};
  }
`;

// ============================================================
//     HELPERS
// ============================================================
const FixedVariantWrapper = (props: {
  children: React.ReactNode;
  isExpanded: boolean;
  isPrimary: boolean;
  widths: NonNullable<Required<ContentWrapperProps['widths']>>;
}) => {
  const {
    children, isExpanded, isPrimary, widths,
  } = props;

  if (!isPrimary) {
    return children as JSX.Element;
  }

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 'calc(var(--cl-zindex-drawer) + 2)',
    }}
    >
      <StyledFixedPlaceholder
        data-component="Sidebar.FixedPlaceholder"
        data-expanded={isExpanded}
        style={
          {
            '--sidebar-width': widths.expanded,
            '--sidebar-width-collapsed': widths.collapsed,
          } as React.CSSProperties
        }
      />
      {children}
    </div>
  );
};

// ============================================================
//     COMPONENT
// ============================================================
/**
 * Wrapper for the sidebar content.
 *
 * Ported from the Frontend-Platform Sidebar wrapper. Renders as a sticky/fixed element on
 * desktop, or a mobile drawer (backdrop + slide-out panel, built from component-library's
 * `Portal`/`FocusTrap` rather than Radix, since Daffy has no radix-ui dependency) when the
 * sidebar is in mobile mode.
 */
export const ContentWrapper = React.forwardRef<HTMLDivElement, ContentWrapperProps>(
  (props, forwardedRef) => {
    const {
      children, style, variant = 'default', widths, ...restProps
    } = props;

    const {
      isExpanded, isMobile, isOpenMobile, setIsOpenMobile,
    } = useSidebarCtx();

    const expandedWidth = widths?.expanded ?? '15rem'; // 240px
    const collapsedWidth = widths?.collapsed ?? '5rem'; // 80px

    const isPrimaryVariant = variant === 'primary';

    // Keeps the drawer mounted for the duration of the exit animation instead of
    // unmounting the instant `isOpenMobile` flips to `false` (there's no Radix Dialog here
    // to coordinate that, since Daffy has no radix-ui dependency).
    const [mobileRenderState, setMobileRenderState] = useState<'closed' | 'closing' | 'open'>(
      isOpenMobile ? 'open' : 'closed',
    );

    useEffect(() => {
      if (!isMobile) {
        setMobileRenderState('closed');
      } else if (isOpenMobile) {
        setMobileRenderState('open');
      } else {
        setMobileRenderState(current => (current === 'closed' ? 'closed' : 'closing'));
      }
    }, [isMobile, isOpenMobile]);

    const handleMobileAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget) return;
      setMobileRenderState(current => (current === 'closing' ? 'closed' : current));
    };

    // Close the mobile drawer on Escape, matching component-library's `Modal` behavior.
    useEffect(() => {
      if (!isMobile || !isOpenMobile) return undefined;

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          setIsOpenMobile(false);
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isMobile, isOpenMobile, setIsOpenMobile]);

    if (isMobile) {
      if (mobileRenderState === 'closed') return null;

      const isClosing = mobileRenderState === 'closing';

      // Falls back to a generic label when the caller doesn't supply one, so the drawer
      // always has an accessible name instead of announcing as an unlabeled dialog.
      const hasAccessibleName = 'aria-label' in restProps || 'aria-labelledby' in restProps;

      return (
        <Portal>
          <StyledMobileWrapper
            {...(hasAccessibleName ? {} : { 'aria-label': 'Sidebar navigation' })}
            {...restProps}
            ref={forwardedRef}
            aria-modal="true"
            data-closing={isClosing}
            data-component="Sidebar.MobileWrapper"
            onAnimationEnd={handleMobileAnimationEnd}
            role="dialog"
            style={
              {
                '--sidebar-width': expandedWidth,
                ...style,
              } as React.CSSProperties
            }
          >
            <FocusTrap>{children}</FocusTrap>
          </StyledMobileWrapper>
          <StyledMobileOverlay
            aria-hidden="true"
            data-closing={isClosing}
            data-component="Sidebar.MobileOverlay"
            onClick={() => setIsOpenMobile(false)}
          />
        </Portal>
      );
    }

    return (
      <FixedVariantWrapper
        isExpanded={isExpanded}
        isPrimary={isPrimaryVariant}
        widths={{ expanded: expandedWidth, collapsed: collapsedWidth }}
      >
        <StyledWrapper
          {...restProps}
          ref={forwardedRef}
          data-component="Sidebar.Wrapper"
          data-expanded={isExpanded}
          data-variant={variant}
          style={
            {
              '--sidebar-width': expandedWidth,
              '--sidebar-width-collapsed': collapsedWidth,
              ...style,
            } as React.CSSProperties
          }
        >
          {children}
        </StyledWrapper>
      </FixedVariantWrapper>
    );
  },
);

ContentWrapper.displayName = 'Sidebar.ContentWrapper' as const;
